/**
 * ADWA HERMI — Product Card Carousel (global)
 * Activates the image carousel + progress bar + swipe on every
 * product card rendered with the `adwa-product-card` snippet.
 *
 * Uses MutationObserver so new cards added dynamically
 * (quick view, cart drawer, AJAX filters, theme editor reloads) also work.
 */

(function () {
  'use strict';

  const INITIALIZED = '__adwaPcardInitialized';

  function initCard(card) {
    if (card[INITIALIZED]) return;
    card[INITIALIZED] = true;

    const carousel = card.querySelector('[data-pcard-carousel]');
    if (!carousel) return;

    const slides  = carousel.querySelectorAll('[data-pcard-slide]');
    const prev    = carousel.querySelector('[data-pcard-prev]');
    const next    = carousel.querySelector('[data-pcard-next]');
    const bar     = card.querySelector('[data-pcard-bar]');
    const barFill = card.querySelector('[data-pcard-bar-fill]');
    const total   = slides.length;
    let index     = 0;

    if (total <= 1) return;

    function go(i) {
      const isForwardWrap  = i >= total;
      const isBackwardWrap = i < 0;
      index = ((i % total) + total) % total;

      slides.forEach((s, si) => s.classList.toggle('is-active', si === index));

      if (barFill) {
        if (isForwardWrap) {
          barFill.style.transition = 'none';
          barFill.style.transform = 'translateX(-100%)';
          void barFill.offsetWidth;
          barFill.style.transition = '';
          barFill.style.transform = 'translateX(0%)';
        } else if (isBackwardWrap) {
          barFill.style.transition = 'none';
          barFill.style.transform = `translateX(${total * 100}%)`;
          void barFill.offsetWidth;
          barFill.style.transition = '';
          barFill.style.transform = `translateX(${(total - 1) * 100}%)`;
        } else {
          barFill.style.transform = `translateX(${index * 100}%)`;
        }
      }

      if (bar) {
        bar.setAttribute('aria-valuenow', index + 1);
        bar.setAttribute('aria-label', `Image ${index + 1} of ${total}`);
      }
    }

    prev?.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      go(index - 1);
    });

    next?.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      go(index + 1);
    });

    bar?.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      const rect = bar.getBoundingClientRect();
      const ratio = (e.clientX - rect.left) / rect.width;
      const target = Math.min(total - 1, Math.max(0, Math.floor(ratio * total)));
      go(target);
    });

    // Swipe on mobile
    let startX = 0, startY = 0, isTracking = false;

    carousel.addEventListener('touchstart', (e) => {
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
      isTracking = true;
    }, { passive: true });

    carousel.addEventListener('touchend', (e) => {
      if (!isTracking) return;
      const dx = e.changedTouches[0].clientX - startX;
      const dy = e.changedTouches[0].clientY - startY;
      if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
        go(dx < 0 ? index + 1 : index - 1);
      }
      isTracking = false;
    }, { passive: true });
  }

  function initAll(root) {
    (root || document).querySelectorAll('[data-pcard]').forEach(initCard);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  // Watch for cards injected later (Shopify theme editor, AJAX results, quick view)
  const observer = new MutationObserver((mutations) => {
    for (const m of mutations) {
      m.addedNodes.forEach((node) => {
        if (!(node instanceof Element)) return;
        if (node.matches?.('[data-pcard]')) initCard(node);
        else initAll(node);
      });
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });

  // Shopify theme editor section reload hook
  document.addEventListener('shopify:section:load', (e) => initAll(e.target));
})();
